import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  Text,
  RefreshControl,
  Platform,
  useWindowDimensions,
  Pressable,
  Animated,
  ViewToken,
  AppState,
  AppStateStatus,
  ScrollView,
} from 'react-native';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import PostListItem from '../../components/PostListItem';
import ObservablePostItem from '../../components/ObservablePostItem';
import LeftSidebar from '../../components/LeftSidebar';
import RightSidebar from '../../components/RightSidebar';
import PixelContainer from '../../components/PixelContainer';
import { fetchPosts } from '../../services/postService';
import { Post } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';
import { useOptionalVideoContext } from '../../contexts/VideoContext';
import { preloadImages } from '../../utils/imageCache';
import { preloadImageDimensions } from '../../utils/imageDimensionsCache';
import { fontStyle } from '../../config/fonts';

const WEB_SIDEBAR_BREAKPOINT = 1024; // Show sidebars on screens wider than this

export default function HomeScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const showSidebars = Platform.OS === 'web' && windowWidth >= WEB_SIDEBAR_BREAKPOINT;
  const videoContext = useOptionalVideoContext();

  const [newPostsCount, setNewPostsCount] = useState(0);
  const [showNewPostsNotification, setShowNewPostsNotification] = useState(false);
  const [cachedPosts, setCachedPosts] = useState<Post[]>([]); // Posts before refetch
  const [hasPendingNewPosts, setHasPendingNewPosts] = useState(false); // Track if new posts are pending user action
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const lastSeenTimestampRef = useRef<number | null>(null); // Timestamp of newest post when user last saw
  const isBackgroundRefetchRef = useRef(false); // Track if refetch was from background
  const flatListRef = useRef<FlatList>(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  const categories = ['All', 'Community', 'Tips & Guides', 'News', 'Help', 'Mods', 'Showcase', 'Tools'];

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ['posts'],
    queryFn: ({ pageParam }) => fetchPosts(pageParam),
    initialPageParam: null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.lastDoc : undefined,
  });

  const posts = data?.pages.flatMap((page) => page.posts) || [];

  // Display cached posts when there are pending new posts (user hasn't clicked notification yet)
  const displayedPosts = hasPendingNewPosts && cachedPosts.length > 0 ? cachedPosts : posts;

  // Initialize the last seen timestamp when posts first load
  useEffect(() => {
    if (posts.length > 0 && lastSeenTimestampRef.current === null) {
      const newestPost = posts[0];
      if (newestPost?.createdAt) {
        lastSeenTimestampRef.current = newestPost.createdAt.toMillis?.() || newestPost.createdAt.seconds * 1000;
      }
    }
  }, [posts]);

  // Handle app focus/visibility changes
  useEffect(() => {
    const handleAppFocus = () => {
      // Mark that the next refetch is from background focus
      isBackgroundRefetchRef.current = true;
      // Cache current posts before refetch
      if (posts.length > 0) {
        setCachedPosts(posts);
      }
      refetch();
    };

    if (Platform.OS === 'web') {
      // Web: use visibilitychange event
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          handleAppFocus();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    } else {
      // Native: use AppState
      const subscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
        if (nextAppState === 'active') {
          handleAppFocus();
        }
      });
      return () => subscription?.remove();
    }
  }, [posts, refetch]);

  // Detect new posts after background refetch completes
  useEffect(() => {
    if (!isRefetching && isBackgroundRefetchRef.current && posts.length > 0) {
      isBackgroundRefetchRef.current = false;

      // Compare with last seen timestamp to find genuinely new posts
      if (lastSeenTimestampRef.current !== null) {
        const newPosts = posts.filter(post => {
          const postTimestamp = post.createdAt?.toMillis?.() || (post.createdAt?.seconds || 0) * 1000;
          return postTimestamp > lastSeenTimestampRef.current!;
        });

        if (newPosts.length > 0) {
          setNewPostsCount(newPosts.length);
          setShowNewPostsNotification(true);
          setHasPendingNewPosts(true); // Keep showing cached posts until user clicks

          // Fade in animation
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
          }).start();
        } else {
          // No new posts, clear cached posts
          setCachedPosts([]);
        }
      }
    }
  }, [isRefetching, posts, fadeAnim]);

  // Preload images and their dimensions from the most recent page for smoother scrolling
  useEffect(() => {
    if (data?.pages && data.pages.length > 0) {
      const latestPage = data.pages[data.pages.length - 1];
      const imageUris = latestPage.posts
        .map(post => post.image)
        .filter((uri): uri is string => !!uri);

      if (imageUris.length > 0) {
        // Preload images in the background (non-blocking)
        preloadImages(imageUris).catch(err =>
          console.warn('[HomeScreen] Image preload failed:', err)
        );
        // Preload image dimensions for smooth navigation to post details
        preloadImageDimensions(imageUris).catch(err =>
          console.warn('[HomeScreen] Dimension preload failed:', err)
        );
      }
    }
  }, [data?.pages]);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Handle scroll-based pagination for web layout
  const handleWebScroll = useCallback((event: any) => {
    if (!hasNextPage || isFetchingNextPage) return;
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const paddingToBottom = 200;
    if (layoutMeasurement.height + contentOffset.y >= contentSize.height - paddingToBottom) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleNewPostsClick = () => {
    // Update the last seen timestamp to the newest post
    if (posts.length > 0) {
      const newestPost = posts[0];
      if (newestPost?.createdAt) {
        lastSeenTimestampRef.current = newestPost.createdAt.toMillis?.() || newestPost.createdAt.seconds * 1000;
      }
    }

    // Clear pending state to show new posts
    setHasPendingNewPosts(false);
    setCachedPosts([]);

    // Fade out animation
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start(() => {
      setShowNewPostsNotification(false);
      setNewPostsCount(0);
    });

    // Scroll to top to show new posts (for mobile FlatList)
    flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
  };

  const handleDismissNotification = () => {
    // Hide notification and show new posts, but DON'T update last seen timestamp
    // User will see the notification again on next focus if they didn't explicitly click it
    setHasPendingNewPosts(false);
    setCachedPosts([]);

    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start(() => {
      setShowNewPostsNotification(false);
      setNewPostsCount(0);
    });
  };

  const handleRefresh = async () => {
    // Manual refresh - update last seen timestamp and clear any pending state
    setHasPendingNewPosts(false);
    setCachedPosts([]);

    await refetch();
    if (posts.length > 0) {
      const newestPost = posts[0];
      if (newestPost?.createdAt) {
        lastSeenTimestampRef.current = newestPost.createdAt.toMillis?.() || newestPost.createdAt.seconds * 1000;
      }
    }
    setShowNewPostsNotification(false);
    setNewPostsCount(0);
  };

  const renderItem = useCallback(({ item }: { item: Post }) =>
    <PostListItem post={item} />,
    []);

  // Viewability config for video autoplay - 50% visible = viewable
  const viewabilityConfig = useRef({
    viewAreaCoveragePercentThreshold: 50,
    minimumViewTime: 300,
  }).current;

  // Store videoContext in ref to avoid changing the callback
  const videoContextRef = useRef(videoContext);
  videoContextRef.current = videoContext;

  // Track which video post is visible for autoplay - must be stable reference
  const onViewableItemsChanged = useRef(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    const ctx = videoContextRef.current;
    if (!ctx) return;

    // Find the first visible post with a video
    const visibleVideoPost = viewableItems.find(
      (item) => item.isViewable && item.item?.video
    );

    if (visibleVideoPost) {
      ctx.setVisiblePostId(visibleVideoPost.item.id);
    } else {
      ctx.setVisiblePostId(null);
    }
  }).current;

  const renderFooter = () => {
    if (!isFetchingNextPage) return null;
    return (
      <View style={styles.footer}>
        <ActivityIndicator size="small" color="#0045AC" />
      </View>
    );
  };

  const renderEmpty = () => {
    if (isLoading) return null;
    return (
      <View style={styles.emptyContainer}>
        <Text style={[styles.emptyTitle, fontStyle, { color: colors.text }]}>{t('empty.noPosts')}</Text>
        <Text style={[styles.emptySubtitle, fontStyle, { color: colors.textSecondary }]}>{t('empty.beFirst')}</Text>
      </View>
    );
  };

  const renderCategoryFilter = () => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.categoriesContainer}
      contentContainerStyle={styles.categoriesContent}
    >
      {categories.map((category) => {
        const isSelected = selectedCategory === category || (category === 'All' && selectedCategory === null);
        return (
          <Pressable
            key={category}
            onPress={() => setSelectedCategory(category === 'All' ? null : category)}
          >
            <PixelContainer
              backgroundColor={isSelected ? colors.primary : colors.surface}
              cornerSize={3}
              style={styles.categoryChip}
            >
              <Text
                style={[
                  styles.categoryChipText,
                  fontStyle,
                  { color: colors.text },
                  isSelected && { color: '#fff' },
                ]}
              >
                {category}
              </Text>
            </PixelContainer>
          </Pressable>
        );
      })}
    </ScrollView>
  );

  if (isLoading) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.background }]}>
        <Text style={[styles.errorText, fontStyle, { color: colors.error }]}>{t('errors.loadFailed')}</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* New posts notification - floating */}
      {showNewPostsNotification && (
        <Animated.View
          style={[
            styles.newPostsNotification,
            {
              backgroundColor: colors.primary,
              opacity: fadeAnim,
              transform: [{
                translateY: fadeAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-50, 0],
                }),
              }],
            },
          ]}
        >
          <View style={styles.newPostsContainer}>
            <Pressable
              onPress={handleNewPostsClick}
              style={styles.newPostsButton}
            >
              <Text style={[styles.newPostsText, fontStyle]}>
                {`${t('home.newPostsAvailable')} (${newPostsCount})`}
              </Text>
            </Pressable>
            <Pressable
              onPress={handleDismissNotification}
              style={styles.dismissButton}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="close" size={18} color="#FFFFFF" />
            </Pressable>
          </View>
        </Animated.View>
      )}

      {showSidebars ? (
        // Web layout with sidebars - parent scroll, sticky sidebars
        <ScrollView
          style={styles.webScrollContainer}
          contentContainerStyle={styles.webScrollContent}
          showsVerticalScrollIndicator={true}
          onScroll={handleWebScroll}
          scrollEventThrottle={100}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={handleRefresh}
              colors={[colors.text]}
              tintColor={colors.text}
            />
          }
        >
          <View style={styles.webLayout}>
            {/* Left Sidebar - Sticky */}
            <View style={styles.stickySidebar}>
              <LeftSidebar />
            </View>

            {/* Main Content Area */}
            <View style={styles.mainContent}>
              {displayedPosts.map((item) => (
                <ObservablePostItem key={item.id} post={item} />
              ))}
              {isFetchingNextPage && renderFooter()}
              {displayedPosts.length === 0 && renderEmpty()}
            </View>

            {/* Right Sidebar - Sticky */}
            <View style={styles.stickySidebar}>
              <RightSidebar />
            </View>
          </View>
        </ScrollView>
      ) : (
        // Mobile layout (no sidebars)
        <View style={styles.contentWrapper}>
          {/* Category Filters */}
          {renderCategoryFilter()}
          <FlatList
            ref={flatListRef}
            data={displayedPosts}
            renderItem={renderItem}
            keyExtractor={(item) => item.id}
            onEndReached={handleLoadMore}
            onEndReachedThreshold={0.5}
            ListFooterComponent={renderFooter}
            ListEmptyComponent={renderEmpty}
            contentContainerStyle={displayedPosts.length === 0 && styles.emptyList}
            viewabilityConfig={viewabilityConfig}
            onViewableItemsChanged={onViewableItemsChanged}
            refreshControl={
              <RefreshControl
                refreshing={isRefetching}
                onRefresh={handleRefresh}
                colors={[colors.text]}
                tintColor={colors.text}
              />
            }
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  webScrollContainer: {
    flex: 1,
  },
  webScrollContent: {
    flexGrow: 1,
    alignItems: 'center',
  },
  webLayout: {
    flexDirection: 'row',
    width: '100%',
    maxWidth: 1220, // 240 (left) + 680 (center) + 300 (right)
  },
  stickySidebar: {
    ...(Platform.OS === 'web' && {
      // @ts-ignore - 'sticky' is valid on web but not in RN types
      position: 'sticky',
      top: 0,
      alignSelf: 'flex-start',
      height: 'auto',
    }),
  },
  contentWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      maxWidth: 680,
      width: '100%',
      alignSelf: 'center',
    }),
  },
  mainContent: {
    flex: 1,
    maxWidth: 680,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  newPostsNotification: {
    position: 'absolute',
    top: 16,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 1000,
  },
  newPostsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  newPostsButton: {
    paddingVertical: 10,
    paddingLeft: 16,
    paddingRight: 8,
  },
  dismissButton: {
    paddingVertical: 10,
    paddingRight: 12,
    paddingLeft: 4,
  },
  newPostsText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  footer: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    textAlign: 'center',
  },
  emptyList: {
    flex: 1,
  },
  errorText: {
    fontSize: 16,
  },
  categoriesContainer: {
    marginTop: 12,
    marginBottom: 8,
  },
  categoriesContent: {
    paddingHorizontal: 16,
    gap: 8,
  },
  categoryChip: {
    paddingHorizontal: 14,
    paddingVertical: 4,
  },
  categoryChipText: {
    fontSize: 13,
    fontWeight: '700',
  },
});
